import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    WritableSignal
} from '@angular/core';
import { AvatarBaseDirective, AvatarDirective, AvatarNameDirective, AvatarOrgDirective } from '@siemens/component-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../auth/services/auth.service';
import { take, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { UserCacheService } from '../../services/user-cache.service';

@Component({
    selector: 'app-user-profile',
    imports: [AvatarDirective, AvatarBaseDirective, AvatarNameDirective, AvatarOrgDirective, TranslatePipe],
    templateUrl: './user-profile.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent implements OnInit, OnDestroy {
    public readonly userId = input<string | undefined>();
    public readonly showData = input<boolean>(false);

    protected authService = inject(AuthService);
    protected userCacheService = inject(UserCacheService);

    private _userPhoto: WritableSignal<Blob | null> = signal<Blob | null>(null);
    private _userName: WritableSignal<string | null> = signal<string | null>(null);
    private _userOrgCode: WritableSignal<string | null> = signal<string | null>(null);
    private destroy$: Subject<boolean> = new Subject<boolean>();

    ngOnInit(): void {
        this.getUserDetails();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private getUserDetails(): void {
        const userId = this.userId();
        if (userId) {
            const userCache = this.userCacheService.getCachedUserData(userId);
            if (userCache) {
                this._userPhoto.set(userCache.photo);
                this._userName.set(userCache.userName);
                this._userOrgCode.set(userCache.orgCode);
            } else {
                this.userCacheService
                    .loadUserData(userId)
                    .pipe(
                        take(1),
                        tap(userCache => {
                            if (userCache) {
                                this._userPhoto.set(userCache.photo);
                                this._userName.set(userCache.userName);
                                this._userOrgCode.set(userCache.orgCode);
                            } else {
                                console.warn(`No photo found for user ID: ${userId}`);
                            }
                        }),
                        takeUntil(this.destroy$)
                    )
                    .subscribe();
            }
        }
    }

    public getUserPhotoUrl(): string | null {
        if (!this.userId()) {
            return this.authService.getUserPhotoUrl();
        }

        const photo = this._userPhoto();
        return photo ? URL.createObjectURL(photo) : null;
    }

    public getUserName(): string | null {
        if (!this.userId()) {
            return this.authService.userName();
        }

        return this._userName();
    }

    public getUserOrgCode(): string | null {
        if (!this.userId()) {
            return this.authService.userIdTokenClaims()?.org_code || null;
        }

        const orgCode = this._userOrgCode() ?? '';
        return this.extractOrgCode(orgCode);
    }

    private extractOrgCode(input: string): string | null {
        const match = input.match(/\(([^)]+)\)/);
        return match ? match[1] : null;
    }
}
